/**
 * A toast — the pilot's `#toast`: a short line at the bottom of the screen
 * that says something happened and then goes away ("Link copied").
 *
 * **Never blocking and never the only way to learn something that matters.**
 * It takes no focus, it covers nothing you need, and it leaves by itself — so
 * it is for confirmations, not for errors a person has to act on (those are
 * `ErrorState`, beside the thing that failed).
 *
 *  - `role="status"` (polite) for news; `tone: 'error'` is `role="alert"`.
 *  - Gone after `duration` (4s by default — the pilot's 1.9s is too short to
 *    read a sentence at 04:00), and **paused while hovered or focused**, so a
 *    person reading it or reaching for its ✕ does not lose it mid-way.
 *  - Stacked at the bottom, above the safe area; newest last.
 *
 * One `ToastProvider`, mounted once in the app shell. `useToast()` outside it
 * is a no-op rather than a crash: a confirmation is never worth an error
 * screen, and a design preview has no provider.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { useUiT } from './text';

export type ToastTone = 'info' | 'error';

export interface ToastOptions {
  tone?: ToastTone;
  /** How long it stays, in ms, not counting time paused. */
  duration?: number;
}

export interface ToastApi {
  /** Show `message`; returns its id, for `dismiss`. */
  show: (message: string, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

export const TOAST_DURATION = 4000;

interface Item { id: number; message: string; tone: ToastTone; duration: number }

const noop: ToastApi = { show: () => -1, dismiss: () => {} };
const ToastContext = createContext<ToastApi>(noop);

export function useToast(): ToastApi {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message: string, options: ToastOptions = {}) => {
    const id = next.current++;
    const item: Item = { id, message, tone: options.tone ?? 'info', duration: options.duration ?? TOAST_DURATION };
    // The same words again replace the old one rather than stacking a twin.
    setItems((list) => [...list.filter((t) => t.message !== message), item]);
    return id;
  }, []);

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="ui-toasts">
        {items.map((t) => <ToastItem key={t.id} item={t} onDone={dismiss} />)}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ item, onDone }: { item: Item; onDone: (id: number) => void }) {
  const tr = useUiT();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;
  const left = useRef(item.duration);

  // Counts down only while not paused; a pause keeps what was left.
  useEffect(() => {
    if (paused) return;
    const started = Date.now();
    const timer = setTimeout(() => onDone(item.id), left.current);
    return () => {
      clearTimeout(timer);
      left.current = Math.max(0, left.current - (Date.now() - started));
    };
  }, [paused, item.id, onDone]);

  const error = item.tone === 'error';
  return (
    <div
      role={error ? 'alert' : 'status'}
      aria-live={error ? 'assertive' : 'polite'}
      aria-atomic="true"
      data-tone={item.tone}
      className="ui-toast"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false); }}
    >
      <span className="ui-toast-text">{item.message}</span>
      <button type="button" onClick={() => onDone(item.id)} aria-label={tr('Close')} className="ui-toast-close">✕</button>
    </div>
  );
}
